import { useState } from 'react';
import { useLoaderData, useNavigate, useRevalidator, useSearchParams } from 'react-router';
import type { LoaderFunctionArgs } from 'react-router';

import { Button, Icon, IconButton } from '@ds/core';
import { Select } from '@ds/forms';
import { SegmentedControl } from '@ds/navigation';
import { PageHeader } from '@/comuns/PageHeader';
import { diaIso, diasDaGrade, mesEAno, somarDias } from '@/comuns/formato';
import { dados } from '@/data/porta';
import type { AgendaDaSemana, Agendamento, StatusDoAgendamento, VinculoDoAgendamento } from '@/data/tipos';
import { useRotaAtual } from '@/router/useRotaAtual';
import { GradeDaAgenda } from './agenda/GradeDaAgenda';
import type { ColunaDaGrade } from './agenda/GradeDaAgenda';
import { GradeDoMes } from './agenda/GradeDoMes';
import { DetalheDoAgendamento } from './agenda/DetalheDoAgendamento';
import { NovoAgendamentoModal } from './NovoAgendamentoModal';

/**
 * Dia de referência vindo da URL.
 *
 * A URL é digitável: `?data=ontem` viraria "Invalid Date", a grade apareceria
 * vazia e nada diria por quê. Data que não existe volta para hoje.
 */
export function diaDaUrl(bruto: string | null): string {
    if (bruto && /^\d{4}-\d{2}-\d{2}$/.test(bruto) && !Number.isNaN(Date.parse(bruto))) return bruto;
    return diaIso();
}

export async function carregarAgenda({ request }: LoaderFunctionArgs): Promise<AgendaDaSemana> {
    return dados().agendaDaSemana(diaDaUrl(new URL(request.url).searchParams.get('data')));
}

const STATUS_FILTRAVEIS: { id: StatusDoAgendamento; rotulo: string; cor: string }[] = [
    { id: 'confirmado', rotulo: 'Confirmado', cor: 'var(--success)' },
    { id: 'pendente', rotulo: 'Pendente', cor: 'var(--warning)' },
    { id: 'concluido', rotulo: 'Concluído', cor: 'var(--info)' },
    { id: 'cancelado', rotulo: 'Cancelado', cor: 'var(--neutral-400)' },
    { id: 'faltou', rotulo: 'Faltou', cor: 'var(--warning-strong)' },
];

const TODOS = STATUS_FILTRAVEIS.map((s) => s.id);

export function Agenda() {
    const semana = useLoaderData() as AgendaDaSemana;
    const [params, setParams] = useSearchParams();
    const navigate = useNavigate();
    const revalidator = useRevalidator();
    const handle = useRotaAtual();
    const [aberto, setAberto] = useState<Agendamento | null>(null);

    const visao = handle?.subrota?.path ?? 'semana';
    const referencia = diaDaUrl(params.get('data'));
    const profissional = params.get('prof') ?? 'todos';
    const sala = params.get('sala') ?? 'todas';
    const statusAtivos = (params.get('status')?.split(',').filter(Boolean) as StatusDoAgendamento[] | undefined) ?? TODOS;

    /* Os filtros vivem na URL, não em estado: assim a agenda de uma sala numa
       semana específica é um link que se manda para a recepção — e sobrevive
       ao F5, que era um dos "não" do docs/ROUTE_MAP.md. */
    const trocarParam = (chave: string, valor: string | null) => {
        setParams((atuais) => {
            const proximos = new URLSearchParams(atuais);
            if (valor === null) proximos.delete(chave);
            else proximos.set(chave, valor);
            return proximos;
        }, { replace: true });
    };

    const irParaVisao = (nova: string) => navigate({ pathname: `/app/agenda/${nova}`, search: params.toString() ? `?${params}` : '' });

    const soBloqueios = visao === 'bloqueios';
    const porSala = visao === 'salas';

    const visiveis = semana.agendamentos.filter((a) => {
        if (a.status === 'bloqueio') return soBloqueios || !porSala;
        if (soBloqueios) return false;
        if (!statusAtivos.includes(a.status)) return false;
        if (profissional !== 'todos' && a.profissional !== profissional) return false;
        if (sala !== 'todas' && a.sala !== sala) return false;
        return true;
    });

    const dias = diasDaGrade(semana.inicio, 6);
    const diaAtivo = dias.some((d) => d.iso === referencia) ? referencia : (dias[0]?.iso ?? semana.inicio);

    let colunas: ColunaDaGrade[];
    let colunaDo: (a: Agendamento) => string | undefined;

    if (porSala) {
        /* Visão por recurso: as colunas viram salas de um dia só. É o que a
           subrota "salas" promete — no protótipo ela apenas abria os filtros. */
        colunas = semana.salas.map((s) => ({ chave: s.nome, titulo: s.nome }));
        colunas.push({ chave: '', titulo: 'Sem sala', atenuada: true });
        colunaDo = (a) => (a.inicioEm.startsWith(diaAtivo) ? a.sala ?? '' : undefined);
    } else if (visao === 'dia') {
        const dia = dias.find((d) => d.iso === diaAtivo);
        colunas = [{ chave: diaAtivo, titulo: String(dia?.numero ?? ''), subtitulo: dia?.rotuloCurto, destacada: dia?.ehHoje }];
        colunaDo = (a) => a.inicioEm.slice(0, 10);
    } else {
        colunas = dias.map((d) => ({ chave: d.iso, titulo: String(d.numero), subtitulo: d.rotuloCurto, destacada: d.ehHoje, atenuada: d.ehFimDeSemana }));
        colunaDo = (a) => a.inicioEm.slice(0, 10);
    }

    const passo = visao === 'dia' || porSala ? 1 : visao === 'mes' ? 30 : 7;
    const contagem = visiveis.filter((a) => a.status !== 'bloqueio').length;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
            <PageHeader
                title="Agenda"
                description={`${mesEAno(semana.inicio)} · ${profissional === 'todos' ? `${semana.profissionais.length} profissionais` : profissional} · ${sala === 'todas' ? `${semana.salas.length} salas` : sala}`}
                actions={
                    /* O modal mora na URL, como o docs/ROUTE_MAP.md pede: assim ele
                       sobrevive ao F5 e o link abre a tela já com ele aberto. */
                    <Button iconLeft={<Icon name="plus" size={15} />} onClick={() => trocarParam('modal', 'novo')}>Novo agendamento</Button>
                }
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <Button variant="secondary" size="sm" onClick={() => trocarParam('data', null)}>Hoje</Button>
                    <div style={{ display: 'flex', gap: 4 }}>
                        <IconButton icon={<Icon name="chevron-left" size={15} />} label="Período anterior" variant="outline" onClick={() => trocarParam('data', somarDias(referencia, -passo))} />
                        <IconButton icon={<Icon name="chevron-right" size={15} />} label="Próximo período" variant="outline" onClick={() => trocarParam('data', somarDias(referencia, passo))} />
                    </div>
                    <Select
                        value={profissional} onChange={(v) => trocarParam('prof', v === 'todos' ? null : v)} size="sm" style={{ width: 190 }}
                        aria-label="Filtrar por profissional"
                        options={[{ value: 'todos', label: 'Todos os profissionais' }, ...semana.profissionais.map((p) => ({ value: p.nome, label: p.nome }))]}
                    />
                    <Select
                        value={sala} onChange={(v) => trocarParam('sala', v === 'todas' ? null : v)} size="sm" style={{ width: 150 }}
                        aria-label="Filtrar por sala"
                        options={[{ value: 'todas', label: 'Todas as salas' }, ...semana.salas.map((s) => ({ value: s.nome, label: s.nome }))]}
                    />
                    <span style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>
                        {contagem} {contagem === 1 ? 'agendamento' : 'agendamentos'}
                    </span>
                    <span style={{ marginLeft: 'auto' }} />
                    <SegmentedControl
                        value={visao} onChange={irParaVisao} ariaLabel="Visão da agenda"
                        options={[
                            { value: 'dia', label: 'Dia' },
                            { value: 'semana', label: 'Semana' },
                            { value: 'mes', label: 'Mês' },
                            { value: 'salas', label: 'Salas' },
                            { value: 'bloqueios', label: 'Bloqueios' },
                        ]}
                    />
                </div>

                {!soBloqueios && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '12px 14px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)' }}>
                        <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: 'var(--tracking-wide)', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>Situação</span>
                        {STATUS_FILTRAVEIS.map((s) => {
                            const ligado = statusAtivos.includes(s.id);
                            return (
                                <button
                                    key={s.id} type="button" aria-pressed={ligado}
                                    onClick={() => {
                                        const proximos = ligado ? statusAtivos.filter((x) => x !== s.id) : [...statusAtivos, s.id];
                                        trocarParam('status', proximos.length === TODOS.length ? null : proximos.join(','));
                                    }}
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 6, height: 26, padding: '0 10px', borderRadius: 'var(--radius-pill)', cursor: 'pointer',
                                        border: `1px solid ${ligado ? 'var(--burgundy-200)' : 'var(--border)'}`,
                                        background: ligado ? 'var(--surface-brand-soft)' : 'var(--surface)',
                                        color: ligado ? 'var(--text-brand)' : 'var(--text-secondary)',
                                        fontFamily: 'var(--font-ui)', fontSize: 12.5, fontWeight: ligado ? 600 : 500,
                                    }}
                                >
                                    <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: '50%', background: s.cor }} />
                                    {s.rotulo}
                                </button>
                            );
                        })}
                        <span style={{ marginLeft: 'auto' }} />
                        <Button
                            variant="ghost" size="sm"
                            onClick={() => setParams(new URLSearchParams(), { replace: true })}
                        >
                            Limpar filtros
                        </Button>
                    </div>
                )}
            </PageHeader>

            {visao === 'mes' ? (
                <GradeDoMes
                    diaDeReferencia={referencia} agendamentos={visiveis}
                    onAbrirDia={(dia) => { trocarParam('data', dia); navigate({ pathname: '/app/agenda/dia', search: `?data=${dia}` }); }}
                    onAbrir={setAberto}
                />
            ) : (
                <GradeDaAgenda
                    colunas={colunas} agendamentos={visiveis} colunaDo={colunaDo}
                    horaInicial={semana.horaInicial} horaFinal={semana.horaFinal}
                    onAbrir={setAberto}
                />
            )}

            <NovoAgendamentoModal
                open={params.get('modal') === 'novo'} onClose={() => trocarParam('modal', null)}
                dataPadrao={referencia}
                onCriado={() => revalidator.revalidate()}
            />

            <DetalheDoAgendamento
                agendamento={aberto} profissionais={semana.profissionais} salas={semana.salas}
                onClose={() => setAberto(null)}
                onSalvo={() => revalidator.revalidate()}
                onAbrirPaciente={(pacienteId) => navigate(`/app/contatos/${pacienteId}`)}
                /* Leva à oportunidade de verdade: o CRM abre o lead pela URL. Antes
                   ia para o quadro e dizia "oportunidade aberta" sem abrir nada. */
                onAbrirLead={(vinculo: VinculoDoAgendamento) => navigate(`/app/crm?lead=${encodeURIComponent(vinculo.id)}`)}
            />
        </div>
    );
}
